import { useState } from 'react';
import { emitUserInteractionEvent } from '../../utils/avroEvents';
import { isolateComponent, labelStyles, withModifier, } from '@zapier/style-encapsulation';
import { Animation, Colors, useClickOutside, useTheme, } from '@zapier/design-system';
import { ZapierLogo } from '@zapier/design-system-patterns';
import SkipLink from '../SkipLink';
import { UniversalTopbarLinks, skipLinkHref } from '../../constants';
import AccountActions from './AccountActions';
import SearchToggler from './SearchToggler';
import UniversalTopbarLink from './UniversalTopbarLink';
import SearchForm from './SearchForm';
import { UNIVERSAL_TOPBAR_HEIGHT } from '../../constants';
const Styles = labelStyles('UniversalTopbar', {
    root: [
        {
            display: 'flex',
            justifyContent: 'center',
            backgroundColor: `${Colors.neutral100}`,
            position: 'relative',
            borderBottom: `1px solid ${Colors.neutral300}`,
        },
    ],
    header: (props, { hasSidebar }) => [
        {
            alignItems: 'stretch',
            display: 'grid',
            gridTemplateColumns: 'max-content auto max-content',
            width: '100%',
            backgroundColor: `${Colors.neutral100}`,
        },
        !hasSidebar &&
            withModifier('hasMaxWidth', {
                maxWidth: 1200,
            }),
        withModifier(`${props.size}-${props.height}`, {
            small: {
                gap: 5,
                height: props.height,
                padding: '0 10px',
            },
            medium: {
                gap: '67px',
                height: props.height,
                padding: '0 20px',
                paddingLeft: hasSidebar ? 10 : 20,
            },
        }[props.size]),
    ],
    group: (props, { gap } = {}) => [
        {
            display: 'grid',
            gridAutoFlow: 'column',
            alignItems: 'center',
        },
        withModifier(props.size, {
            small: { gap: 15 },
            medium: { gap: 20 },
        }[props.size]),
        !!gap &&
            withModifier(`gap-${gap}`, {
                gap,
            }),
    ],
    mainNavigationContent: (props) => [
        {
            display: 'flex',
            alignItems: 'stretch',
        },
        !props.isSearchFullWidth &&
            withModifier('offsetParent', {
                position: 'relative',
            }),
    ],
    searchForm: (props, { isSearching }) => [
        {
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: Animation.transitionValue,
            opacity: 0,
            pointerEvents: 'none',
            transform: 'scaleX(0)',
        },
        isSearching &&
            withModifier('searching', {
                opacity: 1,
                pointerEvents: 'auto',
                transform: 'scaleX(1)',
            }),
        props.isSearchFullWidth &&
            withModifier('fullWidth', {
                backgroundColor: `${Colors.neutral100}`,
                padding: '0 10px',
            }),
    ],
    searchToggler: ({ isSearching }) => [
        {
            marginRight: -7,
            transition: Animation.transitionValue,
        },
        isSearching &&
            withModifier('hidden', {
                opacity: 0.2,
                pointerEvents: 'none',
            }),
    ],
    navLinks: {
        lineHeight: 1.5,
    },
    navLinksList: (props) => [
        Object.assign({ display: 'grid', gridGap: 30 }, (props.links && {
            gridTemplateColumns: `repeat(${props.links.length}, max-content)`,
        })),
        props.isSearching &&
            withModifier('searching', {
                opacity: 0,
            }),
    ],
    logoLinksWrapper: (isPlatform) => (Object.assign({ position: 'relative' }, (!isPlatform && {
        marginTop: -5,
    }))),
    skipLink: {
        position: 'absolute',
        top: '100%',
        whiteSpace: 'nowrap',
    },
});
const Links = (props) => {
    var _a;
    const links = (_a = props.links) === null || _a === void 0 ? void 0 : _a.map((link) => (isolateComponent("li", { key: link.text },
        isolateComponent(UniversalTopbarLink, { href: link.href, isSelected: props.selectedHref === link.href, submenu: link.submenu }, link.text))));
    if (!links) {
        return null;
    }
    return (isolateComponent("nav", { "aria-label": "Navigation Links", css: Styles.navLinks },
        isolateComponent("ul", { css: Styles.navLinksList(props) }, links)));
};
const UniversalTopbar = (props) => {
    const { isPlatform } = useTheme();
    const sidebar = props.renderSidebar && props.renderSidebar(props);
    const logoSize = {
        small: isPlatform ? { width: 81, height: 22 } : { width: 71, height: 32 },
        medium: isPlatform ? { width: 81, height: 22 } : { width: 71, height: 32 },
    }[props.size];
    const searchInputId = 'universal-nav-app-search';
    const [isSearching, setIsSearching] = useState(false);
    const onCloseSearch = () => {
        setIsSearching(false);
    };
    const searchForm = props.renderSearchForm({
        isSearching,
        searchInputId,
        onClose: onCloseSearch,
    });
    const [setClickOutsideNode] = useClickOutside({
        onClickOutside: (event) => {
            if (props.onClickOutside)
                props.onClickOutside(event);
        },
    });
    const { links = UniversalTopbarLinks } = props;
    return (isolateComponent("div", { css: Styles.root, ref: (node) => setClickOutsideNode(node) },
        isolateComponent("div", { css: Styles.header(props, { hasSidebar: !!sidebar }), "data-testid": "universalTopbar", onClick: props.onClick },
            isolateComponent("div", { css: Styles.group(props, { gap: 17 }) },
                sidebar,
                props.shouldRenderLogo && (isolateComponent("header", { css: Styles.logoLinksWrapper(isPlatform) },
                    isolateComponent("a", { "aria-label": props.logoAriaLabel, href: props.logoHref, onClick: props.onLogoClick },
                        isolateComponent(ZapierLogo, Object.assign({}, logoSize))),
                    isolateComponent("div", { css: Styles.skipLink },
                        isolateComponent(SkipLink, { href: props.skipLinkHref }))))),
            isolateComponent("div", { css: Styles.mainNavigationContent(props) },
                links.length > 0 && (isolateComponent(Links, Object.assign({}, props, { isSearching: isSearching, links: links }))),
                !!searchForm && (isolateComponent("div", { "aria-label": "Search apps", css: Styles.searchForm(props, { isSearching }), onBlur: () => {
                        setIsSearching(false);
                    }, onFocus: () => {
                        setIsSearching(true);
                        emitUserInteractionEvent({
                            interaction_name: searchInputId,
                            event_action: 'in_view',
                            option_selected: 'Universal Nav Search opened',
                        });
                    }, role: "form" }, searchForm))),
            isolateComponent("div", { "aria-label": "Manage your account and search apps", css: Styles.group(props), role: "form" },
                !!searchForm && (isolateComponent("div", { css: Styles.searchToggler({ isSearching }), "data-testid": "searchToggler" },
                    isolateComponent(SearchToggler, { id: searchInputId, isSearching: false, label: "Search apps\u2026" }))),
                props.renderNotifications(props),
                props.renderAccountMenu(props)))));
};
UniversalTopbar.defaultProps = {
    logoAriaLabel: 'Go to the Zapier Homepage',
    logoHref: '/',
    onLogoClick: () => { },
    renderAccountMenu: (props) => (isolateComponent(AccountActions, { selectedHref: props.selectedHref, shouldRenderSignIn: props.size !== 'small', shouldRenderSignUp: true, size: props.size })),
    renderNotifications: () => null,
    renderSearchForm: (props) => (isolateComponent(SearchForm, { isSearching: props.isSearching, onClose: props.onClose, searchInputId: props.searchInputId })),
    selectedHref: '',
    shouldRenderLogo: true,
    height: UNIVERSAL_TOPBAR_HEIGHT,
    size: 'medium',
    skipLinkHref,
};
export default UniversalTopbar;
//# sourceMappingURL=UniversalTopbar.js.map
// @zapier/universal-layout@5.12.61 (4902ab0c1)
